import { useState, ReactNode } from 'react';
import { ChevronDown, ChevronUp, Edit, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';
import { ThumbnailVariant } from '../types/ThumbnailVariant';
import { EditVariantDialog } from './EditVariantDialog';
import { ViewCTRDialog } from './ViewCTRDialog';

interface ThumbnailVariantsSectionProps {
  title: string;
  metadataType: string;
  variants: ThumbnailVariant[];
  uploadArea: ReactNode; // The DropArea component for uploading
  onEditVariant: (variantId: string, label: string, isActive: boolean) => Promise<void>;
  aspectRatio?: string; // e.g., "4/5", "5/8", "2/1.2", "24/7"
  showId: string; // Add showId for CTR fetching
}

export function ThumbnailVariantsSection({
  title,
  metadataType,
  variants,
  uploadArea,
  onEditVariant,
  aspectRatio = "4/5", // Default aspect ratio
  showId,
}: ThumbnailVariantsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false); // Collapsed by default
  const [editingVariant, setEditingVariant] = useState<ThumbnailVariant | null>(null);
  const [viewingCTRVariant, setViewingCTRVariant] = useState<ThumbnailVariant | null>(null);

  return (
    <div className="space-y-2">
      {/* Expandable Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{title} - Variants</span>
          <span className="text-xs text-muted-foreground">
            ({variants.length} {variants.length === 1 ? 'variant' : 'variants'})
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 rounded-lg border bg-card space-y-6">
          {/* Uploaded Variants - Full Size */}
          {variants.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Uploaded Variants ({variants.length})</h4>
              <div className="flex flex-wrap gap-6">
                {variants.map((variant) => (
                  <div key={variant.id} className="space-y-3">
                    <div className="relative group" style={{ aspectRatio, width: '300px', maxWidth: '100%' }}>
                      {/* Thumbnail Image - Preserves aspect ratio */}
                      <img
                        src={variant.assetUrl || variant.assetId}
                        alt={variant.label || `Container ${variant.containerIndex}`}
                        className="w-full h-full object-cover rounded-lg border"
                        onError={(e) => {
                          console.error(`Failed to load image for variant ${variant.id}:`, variant.assetUrl || variant.assetId);
                          e.currentTarget.style.border = '2px solid red';
                        }}
                      />

                      {/* Status Badge */}
                      <div className="absolute top-2 right-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            variant.isActive
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-500 text-white'
                          }`}
                        >
                          {variant.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      {/* Hover Edit Button */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setEditingVariant(variant)}
                          className="gap-2"
                        >
                          <Edit className="h-3 w-3" />
                          Edit
                        </Button>
                      </div>
                    </div>

                    {/* Variant Info */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-medium text-muted-foreground">
                          #{variant.containerIndex}
                        </div>
                        {variant.label && (
                          <div className="text-xs font-medium" title={variant.label}>
                            {variant.label}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingVariant(variant)}
                          className="flex-1 gap-1"
                        >
                          <Edit className="h-3 w-3" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setViewingCTRVariant(variant)}
                          className="flex-1 gap-1"
                        >
                          <BarChart3 className="h-3 w-3" />
                          View CTR
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Area at the end */}
          <div className="space-y-3 border-t pt-6">
            <h4 className="text-sm font-medium">Add New Variant</h4>
            {uploadArea}
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <EditVariantDialog
        variant={editingVariant}
        open={editingVariant !== null}
        onClose={() => setEditingVariant(null)}
        onSave={onEditVariant}
      />

      {/* View CTR Dialog */}
      {viewingCTRVariant && (
        <ViewCTRDialog
          open={viewingCTRVariant !== null}
          onClose={() => setViewingCTRVariant(null)}
          showId={showId}
          metadataType={metadataType}
          containerIndex={viewingCTRVariant.containerIndex}
        />
      )}
    </div>
  );
}
